import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Calendar,
  CaretDown as ChevronDown,
  CaretLeft as ChevronLeft,
  CaretRight as ChevronRight,
} from '@phosphor-icons/react';
import { HALF_HOUR_TIMES, parseISO, toISO, fmtDisplay } from '../../utils/andysDates';
import './DatePicker.css';

/**
 * DatePicker — кастомный date picker, который рендерит СВОЙ календарь-попап
 * ВНУТРИ виджета (Shadow DOM), вместо нативного `<input type=date>`.
 *
 * Зачем (#1240): на iOS Safari нативный `<input type=date>` игнорирует свои
 * размеры (та же беда, что в #1196/#1197 — из-за неё пришлось ставить
 * `appearance:none`, чтобы поля ужались в строку). Как следствие его ТАП-ЗОНА
 * вылезает за рамку на соседние контролы: тап по селекту места (сверху) или по
 * чипам возраста (снизу) открывает календарь. Репро: iPhone 16, вкладка Rent.
 * Свой `<button>`-триггер + календарь в нашем DOM убирают и нативный оверлей, и
 * перехват тапов; поля остаются в строку.
 *
 * Drop-in замена `DateField`: `value` в формате `YYYY-MM-DD`, `onChange`
 * вызывается с event-like `{ target: { value } }` — существующие хендлеры
 * (`e.target.value`) работают без изменений.
 *
 * @param {object} props
 * @param {string} [props.id]
 * @param {string} [props.value] — `YYYY-MM-DD` или ''.
 * @param {(e: {target: {value: string}}) => void} [props.onChange]
 * @param {string} [props.min] — минимальная дата `YYYY-MM-DD` (дни до неё disabled).
 * @param {string} [props.ariaLabel]
 * @param {'left'|'right'} [props.align] — к какому краю триггера примыкает попап
 *   (right — для второго поля в ряду, чтобы не вылезал за карточку/вьюпорт).
 * @param {string} [props.className]
 *
 * Время (#1469, аренда Andys: от него зависит цена) — в том же попапе, а не
 * отдельным полем. Включается `onTimeChange`:
 * @param {string} [props.time] — `HH:MM`.
 * @param {(time: string) => void} [props.onTimeChange]
 * @param {string[]} [props.timeOptions] — `HH:MM`; по умолчанию каждые 30 минут,
 *   как в Wheelsystems.
 * @param {string} [props.minTime] — в день `min` время не позже этого
 *   недоступно (возврат в день выдачи — только после неё).
 * @param {string} [props.timeLabel] — подпись времени («Pick-up time»).
 * @param {string} [props.note] — пометка о выбранном дне под календарём
 *   (воскресенье у Andys, #1469). Пустая строка — пометки нет, но место под
 *   неё учтено при выборе, вверх или вниз открывать попап.
 * В этом режиме под календарём кнопка времени и «Done», а в поле —
 * «20 Oct · 09:00». Выбор дня попап не закрывает и время не трогает (12:00 по
 * умолчанию); список времени открывается только тапом по кнопке времени и
 * встаёт на место календаря. Это свой список, не нативный <select>: iPhone
 * Safari сам выкатывал колесо времени после тапа по дню (#1469).
 */
const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const noop = () => {};

// Оценка высоты попапа (header + weekday row + до 6 недель + паддинги) — для
// решения, открывать вверх или вниз, когда снизу мало места (#1240). Со
// временем добавляется строка выбора и «Done».
const POPUP_EST_HEIGHT = 340;
const POPUP_TIME_EXTRA = 96;
// Пометка о дне — до двух строк мелким шрифтом.
const POPUP_NOTE_EXTRA = 40;
// Список времени — сеткой по 4 в ряд (стрелки ↑/↓ — на ряд).
const TIME_COLUMNS = 4;

export const DatePicker = ({
  id,
  value = '',
  onChange = noop,
  min,
  ariaLabel,
  align = 'left',
  className = '',
  time = '',
  onTimeChange,
  timeOptions = HALF_HOUR_TIMES,
  minTime,
  timeLabel = 'Time',
  note,
}) => {
  const withTime = typeof onTimeChange === 'function';
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const ref = useRef(null);
  const triggerRef = useRef(null);
  // Список времени — вместо сетки календаря, той же высоты (#1469).
  const [timeOpen, setTimeOpen] = useState(false);
  const [timeListHeight, setTimeListHeight] = useState(null);
  const timeOpenRef = useRef(false);
  timeOpenRef.current = timeOpen;
  const calendarRef = useRef(null);
  const timeButtonRef = useRef(null);
  const timeListRef = useRef(null);
  const uid = useId().replace(/:/g, '');
  // Значение поля — описание кнопки: aria-label заменяет видимый текст, и без
  // этого скринридер на возврате фокуса читал бы только «Pick-up date and time».
  const valueId = `${uid}-value`;
  const timeLabelId = `${uid}-time-label`;
  const timeValueId = `${uid}-time-value`;
  const selected = parseISO(value);
  const minP = parseISO(min);

  // «Сегодня» — для контурного маркера в сетке: ориентир среди серых прошлых
  // дат (когда задан `min`), чтобы было видно, с какого дня можно выбирать (#1255).
  // Считаем per-render (не useMemo): дёшево и не устаревает, если попап открыт
  // через полночь.
  const now = new Date();
  const today = { y: now.getFullYear(), mo: now.getMonth(), d: now.getDate() };

  // Месяц в сетке: месяц выбранной даты, иначе текущий.
  const [view, setView] = useState(() => {
    if (selected) return { y: selected.y, mo: selected.mo };
    const now = new Date();
    return { y: now.getFullYear(), mo: now.getMonth() };
  });

  // При открытии: прыгаем на месяц выбранной даты + решаем направление —
  // открывать вверх, если снизу до края вьюпорта меньше высоты попапа (иначе
  // календарь у нижнего поля Return уходит за экран, #1240).
  // Даты нет, а `min` в более позднем месяце — открываем его, а не месяц
  // целиком из серых дней: в конце месяца у выдачи Andys (послезавтра) и у
  // возврата после выдачи в следующем месяце (#1469).
  // useLayoutEffect (не useEffect): меряем ДО пейнта, чтобы направление было
  // известно на первом кадре и не было мелькания вниз→вверх у поля Return.
  useLayoutEffect(() => {
    if (!open) return;
    if (selected) setView({ y: selected.y, mo: selected.mo });
    else if (minP && (view.y < minP.y || (view.y === minP.y && view.mo < minP.mo))) {
      setView({ y: minP.y, mo: minP.mo });
    }
    const r = ref.current?.getBoundingClientRect();
    if (r && typeof window !== 'undefined') {
      setDropUp(
        window.innerHeight - r.bottom <
          POPUP_EST_HEIGHT +
            (withTime ? POPUP_TIME_EXTRA : 0) +
            (note !== undefined ? POPUP_NOTE_EXTRA : 0)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Закрытие по клику вне (composedPath — Shadow-DOM-safe) и по Esc.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      const path = e.composedPath ? e.composedPath() : [e.target];
      if (ref.current && !path.includes(ref.current)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // Escape closes only the picker: the landing overlay ignores a handled
      // Escape and would otherwise close the whole landing too (#1469). Focus
      // goes back to the field, as after «Done». An open time list closes first.
      e.preventDefault();
      if (timeOpenRef.current) {
        setTimeOpen(false);
        timeButtonRef.current?.focus({ preventScroll: true });
        return;
      }
      setOpen(false);
      triggerRef.current?.focus({ preventScroll: true });
    };
    // pointerdown (не mousedown) + отложенное подключение: тап, открывший пикер,
    // не должен тут же его закрыть на touch (iOS «сразу закрывается», #1248).
    const t = setTimeout(() => document.addEventListener('pointerdown', onDown), 0);
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const cells = useMemo(() => {
    const first = new Date(view.y, view.mo, 1);
    const startDow = (first.getDay() + 6) % 7; // Пн = 0 (европейская неделя)
    const daysInMonth = new Date(view.y, view.mo + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < startDow; i += 1) arr.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) arr.push(d);
    return arr;
  }, [view]);

  const isDisabled = (d) => {
    if (!minP) return false;
    const cur = new Date(view.y, view.mo, d);
    const mn = new Date(minP.y, minP.mo, minP.d);
    return cur < mn;
  };

  const goMonth = (delta) =>
    setView((v) => {
      const mo = v.mo + delta;
      if (mo < 0) return { y: v.y - 1, mo: 11 };
      if (mo > 11) return { y: v.y + 1, mo: 0 };
      return { ...v, mo };
    });

  // Закрываем попап с возвратом фокуса на поле: иначе фокус пропадает вместе с
  // попапом, а с клавиатуры время меняют, открыв поле снова.
  const closeToTrigger = () => {
    setOpen(false);
    triggerRef.current?.focus({ preventScroll: true });
  };

  const pick = (d) => {
    if (d == null || isDisabled(d)) return;
    onChange({ target: { value: toISO(view.y, view.mo, d) } });
    // Со временем попап остаётся, время не трогаем (12:00 по умолчанию): его
    // меняют по желанию кнопкой времени, закрывают «Done» (#1469).
    if (!withTime) closeToTrigger();
  };

  // Кнопка времени: список встаёт на место календаря той же высоты, чтобы
  // попап не прыгал. Снова тап — назад к календарю.
  const toggleTimeList = () => {
    if (!timeOpen) setTimeListHeight(calendarRef.current?.offsetHeight || null);
    setTimeOpen((o) => !o);
  };

  const pickTime = (t) => {
    onTimeChange(t);
    setTimeOpen(false);
    timeButtonRef.current?.focus({ preventScroll: true });
  };

  // Открыли список — выбранное время по центру и в фокусе (клавиатура).
  useLayoutEffect(() => {
    if (!timeOpen) return;
    const list = timeListRef.current;
    const current = list?.querySelector('[aria-selected="true"]') || list?.querySelector('button:not(:disabled)');
    if (!list || !current) return;
    list.scrollTop = current.offsetTop - list.clientHeight / 2 + current.offsetHeight / 2;
    current.focus({ preventScroll: true });
  }, [timeOpen]);

  // Стрелки по сетке времени: ←/→ — соседнее, ↑/↓ — ряд (4 в ряду).
  const onTimeListKeyDown = (e) => {
    const step = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -TIME_COLUMNS, ArrowDown: TIME_COLUMNS }[e.key];
    if (!step) return;
    const buttons = [...e.currentTarget.querySelectorAll('button')];
    let i = buttons.indexOf(e.target);
    if (i < 0) return;
    e.preventDefault();
    for (i += step; i >= 0 && i < buttons.length; i += step) {
      if (!buttons[i].disabled) {
        buttons[i].focus();
        return;
      }
    }
  };

  // Возврат в день выдачи — только позже неё.
  const timeDisabled = (t) => !!minTime && !!min && value === min && t <= minTime;

  const display = () => {
    if (!value) return 'dd.mm.yyyy';
    if (!withTime) return fmtDisplay(value);
    const p = parseISO(value);
    if (!p) return fmtDisplay(value);
    const day = `${p.d} ${MONTHS[p.mo].slice(0, 3)}`;
    return time ? `${day} · ${time}` : day;
  };

  const isSelected = (d) =>
    selected && selected.y === view.y && selected.mo === view.mo && selected.d === d;

  const isToday = (d) => today.y === view.y && today.mo === view.mo && today.d === d;

  return (
    <div className={`date-picker ${className}`.trim()} ref={ref}>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        className="date-picker__trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel}
        aria-describedby={value ? valueId : undefined}
        onClick={() => {
          setTimeOpen(false);
          setOpen((o) => !o);
        }}
      >
        <span
          id={valueId}
          className={`date-picker__value${value ? '' : ' date-picker__value--placeholder'}`}
        >
          {display()}
        </span>
        <Calendar className="date-picker__icon" size={18} weight="regular" aria-hidden="true" />
      </button>

      {open && (
        <div
          className={`date-picker__popup${align === 'right' ? ' date-picker__popup--right' : ''}${dropUp ? ' date-picker__popup--up' : ''}`}
          role="dialog"
          aria-label="Choose date"
        >
          {timeOpen ? (
            <div
              ref={timeListRef}
              className="date-picker__time-list"
              role="listbox"
              aria-labelledby={timeLabelId}
              style={timeListHeight ? { height: timeListHeight } : undefined}
              onKeyDown={onTimeListKeyDown}
            >
              {timeOptions.map((t) => (
                <button
                  key={t}
                  type="button"
                  role="option"
                  aria-selected={t === time}
                  className={`date-picker__time-option${t === time ? ' date-picker__time-option--sel' : ''}`}
                  disabled={timeDisabled(t)}
                  onClick={() => pickTime(t)}
                >
                  {t}
                </button>
              ))}
            </div>
          ) : (
            <div ref={calendarRef} className="date-picker__calendar">
              <div className="date-picker__header">
                <button
                  type="button"
                  className="date-picker__nav"
                  aria-label="Previous month"
                  onClick={() => goMonth(-1)}
                >
                  <ChevronLeft size={18} weight="regular" aria-hidden="true" />
                </button>
                <span className="date-picker__title">
                  {MONTHS[view.mo]} {view.y}
                </span>
                <button
                  type="button"
                  className="date-picker__nav"
                  aria-label="Next month"
                  onClick={() => goMonth(1)}
                >
                  <ChevronRight size={18} weight="regular" aria-hidden="true" />
                </button>
              </div>
  
              <div className="date-picker__grid date-picker__grid--head" aria-hidden="true">
                {WEEKDAYS.map((w) => (
                  <span key={w} className="date-picker__dow">
                    {w}
                  </span>
                ))}
              </div>
  
              <div className="date-picker__grid" role="grid">
                {cells.map((d, i) =>
                  d === null ? (
                    // eslint-disable-next-line react/no-array-index-key
                    <span key={`empty-${i}`} className="date-picker__cell date-picker__cell--empty" />
                  ) : (
                    <button
                      key={d}
                      type="button"
                      className={`date-picker__cell${isSelected(d) ? ' date-picker__cell--sel' : ''}${
                        isToday(d) && !isSelected(d) ? ' date-picker__cell--today' : ''
                      }`}
                      disabled={isDisabled(d)}
                      aria-label={toISO(view.y, view.mo, d)}
                      aria-selected={isSelected(d) || undefined}
                      onClick={() => pick(d)}
                    >
                      {d}
                    </button>
                  ),
                )}
              </div>
            </div>
          )}

          {note && <p className="date-picker__note">{note}</p>}

          {withTime && (
            <div className="date-picker__time-row">
              <div className="date-picker__time">
                <span id={timeLabelId} className="date-picker__time-label">
                  {timeLabel}
                </span>
                <button
                  ref={timeButtonRef}
                  type="button"
                  className="date-picker__time-btn"
                  aria-haspopup="listbox"
                  aria-expanded={timeOpen}
                  aria-labelledby={`${timeLabelId} ${timeValueId}`}
                  onClick={toggleTimeList}
                >
                  <span id={timeValueId}>{time || '--:--'}</span>
                  <ChevronDown size={16} weight="regular" aria-hidden="true" />
                </button>
              </div>
              <button type="button" className="date-picker__done" onClick={closeToTrigger}>
                Done
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DatePicker;
